import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import PopconfirmDeleteIcon from "@/components/icons/PopconfirmDeleteIcon";
import BackNavigationArrow from "@/components/views/BackNavigationArrow";
import { ItemCreatedUpdatedInfoView } from "@/components/views/ItemCreatedUpdatedInfoView";
import { PageHeader } from "@/components/views/PageHeader";
import { useBackNavigation } from "@/utils/hooksUtils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Col, Dropdown, Form, Popconfirm, Radio, Row, Space, Tag } from "antd";
import { FormInstance } from "antd/lib/form";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { generatePath, useNavigate } from "react-router";
import { useDeleteFinancialMediation } from "../../../../../common/api/queries";
import { rowGutter } from "../../../../../common/constants";
import { Permission } from "../../../../../common/security/enums";
import { hasPermission } from "../../../../../common/security/utils";
import { CONTRACT_ROUTE_PATHS } from "../../../../contract/paths";
import { ProductFinancialSector } from "../../../../product/enums";
import { PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP } from "../../../../product/utils";
import { FinancialMediationStatus, FinancialMediationVersion } from "../../../enums";
import { CreateUpdateFinancialMediation, FinancialMediation, FinancialMediationRecommendation } from "../../../types";
import { getAllVersionsForFinancialMediation } from "../../../utils";
import FinancialMediationStatusTag from "../../FinancialMediationStatusTag";
import FinancialMediationVersionTag from "../../FinancialMediationVersionTag";

interface Props {
  form: FormInstance<CreateUpdateFinancialMediation>;
  financialMediation?: FinancialMediation;
  permissions: Permission[];
  onFormSubmit: () => void;
  onGenerateClick: (version: FinancialMediationVersion) => void;
  onChangeVersionClick: () => void;
  onFinishClick: () => void;
  onAssignClick: (status: FinancialMediationStatus) => void;
}

export const FinancialMediationFormHeaderSection = ({ form, financialMediation, permissions, ...props }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const onBackClick = useBackNavigation();

  const deleteFinancialMediation = useDeleteFinancialMediation();

  const allowedSectors = useMemo(() => {
    const sectors: ProductFinancialSector[] = [];

    if (hasPermission(permissions, Permission.INSURANCE_CREATE)) {
      sectors.push(ProductFinancialSector.NON_LIFE_INSURANCES, ProductFinancialSector.LIFE_INSURANCES);
    }
    if (hasPermission(permissions, Permission.LOAN_CREATE)) {
      sectors.push(ProductFinancialSector.LOANS_AND_MORTGAGES);
    }
    if (hasPermission(permissions, Permission.DEPOSIT_CREATE)) {
      sectors.push(ProductFinancialSector.DEPOSITS);
    }
    if (hasPermission(permissions, Permission.SECOND_PILLAR_CREATE)) {
      sectors.push(ProductFinancialSector.SENIOR_PENSION_SAVINGS);
    }
    if (hasPermission(permissions, Permission.THIRD_PILLAR_CREATE)) {
      sectors.push(ProductFinancialSector.SUPPLEMENTARY_PENSION_SAVINGS);
    }

    return sectors;
  }, [permissions]);

  const handleSectorChange = (sector: ProductFinancialSector) => {
    const recommendations = [
      ...(form.getFieldValue(["mediationData", "recommendationData", "recommendations"]) || Array(1).fill({}, 0, 1)),
    ] as FinancialMediationRecommendation[];

    form.setFieldsValue({
      mediationData: {
        recommendationData: {
          recommendations: recommendations.map((recommendation) => ({
            ...recommendation,
            institutionId: undefined,
            productId: undefined,
          })),
        },
        clientRequirements: [],
        clientSpecificRequirements:
          sector === ProductFinancialSector.DEPOSITS ? Array(3).fill({ selected: false }, 0, 3) : undefined,
        investmentQuestionnaireData: undefined,
        sectorAttachments: [],
      },
    });
  };

  const getActionElements = useCallback(() => {
    if (!financialMediation) {
      return [];
    }

    const handleFinishClick = (status: FinancialMediationStatus) => {
      switch (status) {
        case FinancialMediationStatus.ASSIGNED_TO_CONTRACT:
          props.onFinishClick();
          break;
        case FinancialMediationStatus.ASSIGNED_TO_CLIENT:
          props.onAssignClick(status);
          break;
      }
    };

    const actionElements: { key: string; element: React.ReactNode; visibility: boolean }[] = [
      {
        key: "save",
        element: (
          <Button key={0} type="primary" icon={<AntIcon type="save" />} onClick={props.onFormSubmit}>
            {t("common.save")}
          </Button>
        ),
        visibility:
          hasPermission(
            permissions,
            PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP.get(financialMediation.sector) as Permission,
          ) && financialMediation.status === FinancialMediationStatus.UNFINISHED,
      },
      {
        key: "downloadVersion1",
        element: (
          <Button
            key={1}
            type="primary"
            icon={<AntIcon type="file-text" />}
            onClick={() => props.onGenerateClick(FinancialMediationVersion.VERSION_1)}
          >
            {t("common.download")}
          </Button>
        ),
        visibility: financialMediation.version === FinancialMediationVersion.VERSION_1,
      },
      {
        key: "download",
        element: (
          <Dropdown
            disabled={false}
            menu={{
              items: getAllVersionsForFinancialMediation(financialMediation.version).map((version) => ({
                label: t("financialMediation.enums.version." + version),
                key: version,
              })),
              onClick: (event) =>
                props.onGenerateClick(FinancialMediationVersion[event.key as keyof typeof FinancialMediationVersion]),
            }}
          >
            <Button type="primary" disabled={false}>
              <Space>
                {t("common.download")}
                <span>
                  <AntIcon type="down" />
                </span>
              </Space>
            </Button>
          </Dropdown>
        ),
        visibility: financialMediation.version !== FinancialMediationVersion.VERSION_1,
      },
      {
        key: "assignToContract",
        element: (
          <SecondaryButton
            key={3}
            icon={<AntIcon type="delivered-procedure" />}
            disabled={false}
            onClick={() => props.onAssignClick(FinancialMediationStatus.ASSIGNED_TO_CONTRACT)}
          >
            {t("financialMediation.actions.assignToContract")}
          </SecondaryButton>
        ),
        visibility:
          hasPermission(
            permissions,
            PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP.get(financialMediation.sector) as Permission,
          ) &&
          (financialMediation.status === FinancialMediationStatus.FINISHED ||
            financialMediation.status === FinancialMediationStatus.ASSIGNED_TO_CONTRACT),
      },
      {
        key: "changeVersion",
        element: (
          <SecondaryButton key={4} icon={<AntIcon type="node-expand" />} onClick={props.onChangeVersionClick}>
            {t("financialMediation.actions.changeVersion")}
          </SecondaryButton>
        ),
        visibility:
          hasPermission(
            permissions,
            PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP.get(financialMediation.sector) as Permission,
          ) &&
          financialMediation.version !== FinancialMediationVersion.VERSION_5 &&
          financialMediation.status === FinancialMediationStatus.UNFINISHED,
      },
      {
        key: "finish",
        element: (
          <Dropdown
            key={5}
            disabled={false}
            menu={{
              items: [
                {
                  label: t("financialMediation.helpers.contractCreated"),
                  icon: <AntIcon type="file-done" />,
                  key: FinancialMediationStatus.ASSIGNED_TO_CONTRACT,
                },
                {
                  label: t("financialMediation.helpers.contractNotCreated"),
                  icon: <AntIcon type="user-switch" />,
                  key: FinancialMediationStatus.ASSIGNED_TO_CLIENT,
                },
              ],
              onClick: (event) =>
                handleFinishClick(FinancialMediationStatus[event.key as keyof typeof FinancialMediationStatus]),
            }}
          >
            <div>
              <SecondaryButton>
                <Space>
                  <span>{t("financialMediation.actions.finish")}</span>
                  <span>
                    <AntIcon type="down" />
                  </span>
                </Space>
              </SecondaryButton>
            </div>
          </Dropdown>
        ),
        visibility:
          hasPermission(
            permissions,
            PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP.get(financialMediation.sector) as Permission,
          ) && financialMediation.status === FinancialMediationStatus.UNFINISHED,
      },
      {
        key: "delete",
        element: (
          <Popconfirm
            key={6}
            title={t("financialMediation.helpers.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            okButtonProps={{ disabled: false }}
            okType="danger"
            cancelText={t("common.no")}
            cancelButtonProps={{ disabled: false }}
            disabled={false}
            onConfirm={() =>
              deleteFinancialMediation.mutate(financialMediation.id, {
                onSuccess: () => navigate(generatePath(CONTRACT_ROUTE_PATHS.forms.to)),
              })
            }
          >
            <Button danger icon={<AntIcon type="delete" />} disabled={false} />
          </Popconfirm>
        ),
        visibility: hasPermission(
          permissions,
          PRODUCT_SECTOR_TO_UPDATE_CONTRACT_PERMISSION_MAP.get(financialMediation.sector) as Permission,
        ),
      },
    ];

    return actionElements.filter((item) => item.visibility).map((item) => <div key={item.key}>{item.element}</div>);
  }, [financialMediation, permissions]);

  return financialMediation ? (
    <>
      <HiddenInput name="sector" initialValue={financialMediation.sector} />
      <HiddenInput name="validateAllFields" initialValue={false} />

      <PageHeader
        title={t("financialMediation.titles.detail")}
        tags={[
          <Tag key={0} color="purple">
            {t("product.enums.financialSector." + financialMediation.sector)}
          </Tag>,
          <FinancialMediationVersionTag key={1} version={financialMediation.version} />,
          <FinancialMediationStatusTag key={2} status={financialMediation.status} />,
        ]}
        extra={<Space>{getActionElements()}</Space>}
        onBack={onBackClick}
      >
        <ItemCreatedUpdatedInfoView item={financialMediation} style={{ marginTop: "-15px" }} />
      </PageHeader>
    </>
  ) : (
    <Card
      className="card-box card-box--inner-extra"
      title={
        <BackNavigationArrow>
          <h2>{t("financialMediation.titles.create")}</h2>
        </BackNavigationArrow>
      }
      extra={
        <Button type="primary" icon={<AntIcon type="save" />} onClick={props.onFormSubmit}>
          {t("common.save")}
        </Button>
      }
    >
      <Row gutter={rowGutter} justify="center">
        <Col span={24} className="center-align">
          <Form.Item name="sector" rules={[validations.notNull]} className="margin-top-small">
            <Radio.Group
              block
              size="large"
              buttonStyle="solid"
              optionType="button"
              onChange={(e) => handleSectorChange(e.target.value)}
            >
              {allowedSectors.map((sector) => (
                <Radio.Button value={sector} key={sector}>
                  <span style={{ fontSize: 16, fontWeight: "bold" }}>
                    {t("product.enums.financialSector." + sector)}
                  </span>
                </Radio.Button>
              ))}
            </Radio.Group>
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );
};
